<template>
  <div class="container-fluid dashboard-panel mb-4">
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando dashboard...</p>
    </div>

    <template v-else>
      <div class="view-header my-4">
        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Inicio</h1>
            <p class="view-header__subtitle">Resumen de la actividad reciente.</p>
          </div>
        </div>
      </div>

      <v-guard :permissions="['INICIO_VER_GRAFICO']">
        <div class="dashboard-section">
          <div class="dashboard-section__header">
            <div class="dashboard-section__filters">
              <div v-if="canFilterBranch" class="dashboard-section__filter">
                <select :value="branchoffice" @change="onBranchChange" name="branchoffice" class="form-control">
                  <option value="">Todas las sucursales</option>
                  <option v-for="bo in branchoffices" :key="bo.id" :value="bo.id">{{ bo.name }}</option>
                </select>
              </div>
              <div class="dashboard-section__filter">
                <select :value="filter" @change="onFilterChange" name="filter" class="form-control">
                  <option value="1">Últimos 30 días</option>
                  <option value="3">Últimos 7 días</option>
                  <option value="2">Este mes</option>
                  <option value="4">Esta semana</option>
                  <option value="5">Mes anterior</option>
                </select>
              </div>
            </div>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-card__icon">
                <i class="bi bi-cash-coin"></i>
              </div>
              <div class="stat-card__body">
                <div class="stat-card__label">En ventas {{ filterLabel }}</div>
                <div class="stat-card__value">{{ formatoDinamicoMoneda(monthSales) }}</div>
                <div v-if="salesGrowth !== null" class="stat-card__trend" :class="trendClass(salesGrowth)">
                  <i class="bi" :class="salesGrowth >= 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'"></i>
                  {{ formatGrowth(salesGrowth) }} vs. período anterior
                </div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-card__icon">
                <i class="bi bi-receipt"></i>
              </div>
              <div class="stat-card__body">
                <div class="stat-card__label">Ventas {{ filterLabel }}</div>
                <div class="stat-card__value">{{ monthSalesCount }}</div>
                <div v-if="salesCountGrowth !== null" class="stat-card__trend" :class="trendClass(salesCountGrowth)">
                  <i class="bi" :class="salesCountGrowth >= 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'"></i>
                  {{ formatGrowth(salesCountGrowth) }} vs. período anterior
                </div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-card__icon">
                <i class="bi bi-graph-up-arrow"></i>
              </div>
              <div class="stat-card__body">
                <div class="stat-card__label">Promedio por venta</div>
                <div class="stat-card__value">{{ formatoDinamicoMoneda(promedio) }}</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-card__icon stat-card__icon--warning">
                <i class="bi bi-wallet2"></i>
              </div>
              <div class="stat-card__body">
                <div class="stat-card__label">Por cobrar (cta. cte.)</div>
                <div class="stat-card__value">{{ formatoDinamicoMoneda(totalReceivable) }}</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-card__icon stat-card__icon--danger">
                <i class="bi bi-arrow-counterclockwise"></i>
              </div>
              <div class="stat-card__body">
                <div class="stat-card__label">Notas de crédito {{ filterLabel }}</div>
                <div class="stat-card__value">{{ formatoDinamicoMoneda(creditNotesTotal) }}</div>
                <div class="stat-card__meta">{{ creditNotesCount }} emitida(s)</div>
              </div>
            </div>
          </div>

          <div class="charts-row">
            <div class="chart-card chart-card--main">
              <div class="chart-card__title">Evolución de ventas</div>
              <chartjs-line
                v-if="!loading"
                :key="dataVersion"
                :linetension="0.1"
                :scalesdisplay="false"
                :height="82"
                datalabel="Total de ventas"
                :data="chartData"
                :labels="chartLabels"
                :option="mergedGraphOptions"
                bordercolor="rgba(68, 119, 189,1)"
                backgroundcolor="rgba(68,119,189,0.5)"
                :pointborderwidth="4"
                :pointbordercolor="'rgba(68, 119, 189,1)'"
                :pointhoverborderwidth="3"
                :pointhoverbackgroundcolor="'rgba(68, 119, 189,1)'"
                :pointhoverbordercolor="'rgba(68, 119, 189,1)'"
                :fill="true"
              />
            </div>
            <div class="chart-card chart-card--side">
              <div class="chart-card__title">Ventas por método de pago</div>
              <div class="chart-card__canvas">
                <chartjs-doughnut
                  v-if="!loading && paymentMethods.length"
                  :key="'doughnut-' + dataVersion"
                  :height="180"
                  datalabel="Ventas"
                  :data="paymentMethodData"
                  :labels="paymentMethodLabels"
                  :backgroundcolor="paymentMethodColors"
                  :option="paymentMethodOptions"
                />
                <div v-else class="chart-card__empty">Sin datos en el período</div>
              </div>
            </div>
          </div>
        </div>
      </v-guard>

      <div class="panels-grid mt-4">
        <div class="dash-panel">
          <h2 class="dashboard-section__title">
            <i class="bi bi-receipt dashboard-section__title-icon"></i>
            Últimos 5 comprobantes
          </h2>
          <div class="data-table">
            <div class="table-responsive">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Número</th>
                    <th>Cliente</th>
                    <th>Monto</th>
                    <th class="text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sale in lastFiveSales" :key="sale.id">
                    <td class="data-table__muted">{{ sale.type }} {{ sale.letter }}</td>
                    <td>
                      <a :href="`/ventas/index/detalle/${sale.id}`" class="data-table__primary">#{{ sale.full_number }}</a>
                    </td>
                    <td>{{ sale.client_name || '—' }}</td>
                    <td>{{ formatoDinamicoMoneda(sale.total) }}</td>
                    <td class="text-right">
                      <a :href="`/ventas/index/detalle/${sale.id}`" class="table-action-btn" title="Ver venta">
                        <i class="bi bi-eye"></i>
                      </a>
                    </td>
                  </tr>
                  <tr v-if="lastFiveSales.length === 0">
                    <td colspan="5" class="data-table__empty">
                      Todavía no tenés ventas, realiza la primera <a href="/ventas/index/nueva-venta">desde acá</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="dash-panel">
          <h2 class="dashboard-section__title">
            <i class="bi bi-people dashboard-section__title-icon"></i>
            Clientes con cuenta corriente
            <span v-if="totalReceivable" class="panel__amount">{{ formatoDinamicoMoneda(totalReceivable) }}</span>
          </h2>
          <div class="data-table">
            <div class="table-responsive">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Teléfono</th>
                    <th class="text-right">Saldo</th>
                    <th class="text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="client in topDebtsClients" :key="client.id">
                    <td>
                      <a :href="`/ventas/clientes/cc-clientes/CC-clientes/${client.id}`" class="data-table__primary">{{ client.name }}</a>
                      <div v-if="client.business" class="data-table__secondary">{{ client.business }}</div>
                    </td>
                    <td class="data-table__muted">{{ client.phone || '—' }}</td>
                    <td class="text-right" :class="client.balance > 0 ? 'data-table__danger' : 'data-table__muted'">
                      {{ formatoDinamicoMoneda(client.balance) }}
                    </td>
                    <td class="text-right">
                      <a :href="`/ventas/clientes/cc-clientes/CC-clientes/${client.id}`" class="table-action-btn" title="Ver cuenta">
                        <i class="bi bi-eye"></i>
                      </a>
                    </td>
                  </tr>
                  <tr v-if="topDebtsClients.length === 0">
                    <td colspan="4" class="data-table__empty">Sin clientes en cuenta corriente</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="dash-panel">
          <h2 class="dashboard-section__title">
            <i class="bi bi-trophy dashboard-section__title-icon"></i>
            5 productos más vendidos
          </h2>
          <div class="data-table">
            <div class="table-responsive">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th class="text-right">Unidades</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(productSale, index) in topProductsSales"
                    :key="productSale.id"
                    :class="{ 'top-seller-row': index === 0 }"
                  >
                    <td>
                      <span class="data-table__primary">{{ productSale.name }}</span>
                      <i v-if="index === 0" class="bi bi-trophy-fill top-seller-icon"></i>
                    </td>
                    <td class="text-right">{{ productSale.total }}</td>
                  </tr>
                  <tr v-if="topProductsSales.length === 0">
                    <td colspan="2" class="data-table__empty">
                      Todavía no tenés ventas, realiza la primera <a href="/ventas/index/nueva-venta">desde acá</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="dash-panel">
          <h2 class="dashboard-section__title">
            <i class="bi bi-box-seam dashboard-section__title-icon"></i>
            Productos con stock bajo
            <span v-if="lowStockCount" class="panel__badge">{{ lowStockCount }}</span>
          </h2>
          <div class="data-table">
            <div class="table-responsive">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th class="text-right">Stock</th>
                    <th class="text-right">Mínimo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in lowStockProducts" :key="product.id">
                    <td>
                      <a :href="`/inventario/productos/editar-producto/${product.id}`" class="data-table__primary">{{ product.name }}</a>
                    </td>
                    <td class="text-right data-table__danger">{{ product.total }}</td>
                    <td class="text-right data-table__muted">{{ product.stock_minimum }}</td>
                  </tr>
                  <tr v-if="lowStockProducts.length === 0">
                    <td colspan="3" class="data-table__empty">Todo el stock está por encima del mínimo</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import formatMoneyMixin from '../../mixins/formatMoneyMixin';
import ChartjsLine from '../global/ChartjsLine.vue';
import ChartjsDoughnut from '../global/ChartjsDoughnut.vue';

export default {
  mixins: [formatMoneyMixin],
  components: {
    ChartjsLine,
    ChartjsDoughnut,
  },
  computed: {
    ...mapState('dashboard', ['loading', 'data', 'dataVersion']),
    ...mapGetters('dashboard', [
      'monthSales',
      'monthSalesCount',
      'promedio',
      'salesGrowth',
      'salesCountGrowth',
      'creditNotesTotal',
      'creditNotesCount',
      'totalReceivable',
      'paymentMethods',
      'lowStockProducts',
      'lowStockCount',
      'lastFiveSales',
      'topProductsSales',
      'topDebtsClients',
      'chartData',
      'chartLabels',
      'graphOptions',
      'canFilterBranch',
      'branchoffices',
    ]),
    branchoffice() {
      return this.$route.query.bo || '';
    },
    paymentMethodLabels() {
      return this.paymentMethods.map((row) => row.method);
    },
    paymentMethodData() {
      return this.paymentMethods.map((row) => row.total);
    },
    paymentMethodColors() {
      // Color fijo por método; se cicla si aparecieran más de los listados.
      const palette = {
        Efectivo: '#2bb673',
        Transferencia: '#4477bd',
        Tarjeta: '#f5a623',
        Cheque: '#8a6fd1',
        'Cuenta corriente': '#e0526a',
      };
      const fallback = ['#00a8c6', '#d23f59', '#b8860b'];
      return this.paymentMethods.map((row, i) => palette[row.method] || fallback[i % fallback.length]);
    },
    paymentMethodOptions() {
      const formatMoney = (value) => this.formatoDinamicoMoneda(value);
      return {
        responsive: true,
        maintainAspectRatio: true,
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            fontSize: 11,
            padding: 12,
            // Agrega el % de cada método al texto de la leyenda.
            generateLabels(chart) {
              const data = chart.data;
              if (!data.labels.length || !data.datasets.length) {
                return [];
              }
              const ds = data.datasets[0];
              const total = ds.data.reduce((sum, val) => sum + val, 0);
              const meta = chart.getDatasetMeta(0);
              return data.labels.map((label, i) => {
                const pct = total ? Math.round((ds.data[i] / total) * 100) : 0;
                return {
                  text: `${label} (${pct}%)`,
                  fillStyle: Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor,
                  strokeStyle: ds.borderColor,
                  lineWidth: ds.borderWidth,
                  hidden: isNaN(ds.data[i]) || meta.data[i].hidden,
                  index: i,
                };
              });
            },
          },
        },
        plugins: { datalabels: { display: false } },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              const label = data.labels[tooltipItem.index] || '';
              const value = data.datasets[0].data[tooltipItem.index];
              return `${label}: ${formatMoney(value)}`;
            },
          },
        },
      };
    },
    filter() {
      const queryFilter = Number(this.$route.query.filter);

      return isNaN(queryFilter) ? 1 : queryFilter;
    },
    mergedGraphOptions() {
      const base = this.graphOptions || {};
      const baseTooltips = base.tooltips || {};
      const baseCallbacks = baseTooltips.callbacks || {};
      const formatMoney = (value) => this.formatoDinamicoMoneda(value);
      return {
        ...base,
        tooltips: {
          ...baseTooltips,
          callbacks: {
            ...baseCallbacks,
            label(tooltipItem, data) {
              const dsLabel = (data.datasets[tooltipItem.datasetIndex] || {}).label || '';
              return `${dsLabel}: ${formatMoney(tooltipItem.yLabel)}`;
            },
          },
        },
      };
    },
    filterLabel() {
      if (this.filter === 1) {
        return 'los últimos 30 días';
      }
      if (this.filter === 2) {
        return 'este mes';
      }
      if (this.filter === 3) {
        return 'los últimos 7 días';
      }
      if (this.filter === 4) {
        return 'esta semana';
      }
      if (this.filter === 5) {
        return 'el mes anterior';
      }
      return '';
    },
  },
  mounted() {
    this.reloadDashboard();
  },
  watch: {
    filter() {
      this.reloadDashboard();
    },
    branchoffice() {
      this.reloadDashboard();
    },
  },
  methods: {
    ...mapActions('dashboard', ['getDashboardData']),
    reloadDashboard() {
      this.getDashboardData({ filter: this.filter, branchoffice: this.branchoffice });
    },
    onFilterChange(event) {
      this.$router.push({ query: { ...this.$route.query, filter: event.target.value } });
    },
    onBranchChange(event) {
      this.$router.push({ query: { ...this.$route.query, bo: event.target.value } });
    },
    formatGrowth(value) {
      if (value === null) {
        return '';
      }
      // Cuando el período anterior fue casi nulo el % se dispara y no aporta;
      // lo acotamos para no mostrar precisión absurda (ej: +596666.5%).
      if (value > 999) {
        return '+999%';
      }
      if (value < -999) {
        return '-999%';
      }
      const sign = value >= 0 ? '+' : '';
      const decimals = Math.abs(value) >= 100 ? 0 : 1;
      return `${sign}${value.toFixed(decimals)}%`;
    },
    trendClass(value) {
      if (value === null) {
        return '';
      }
      return value >= 0 ? 'stat-card__trend--up' : 'stat-card__trend--down';
    },
  },
};
</script>

<style lang="scss" scoped>
$dp-border: var(--border-soft);
$dp-muted: var(--text-muted);
$dp-text: var(--text);
$dp-accent: #4477bd; // estático: se usa dentro de rgba(); es color de marca

.dashboard-panel {
  .dashboard-section {
    margin-bottom: 32px;
  }

  .dashboard-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  .dashboard-section__title {
    font-size: 14px;
    font-weight: 600;
    color: $dp-text;
    margin: 0 0 12px;
    text-transform: none;
    letter-spacing: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dashboard-section__title-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba($dp-accent, 0.12);
    color: $dp-accent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 1;
  }

  .dashboard-section__filters {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .dashboard-section__filter {
    margin: 0;

    select.form-control {
      height: 34px;
      padding: 4px 28px 4px 12px;
      font-size: 13px;
      color: $dp-text;
      background-color: var(--surface);
      border: 1px solid $dp-border;
      border-radius: 8px;
      box-shadow: none;
      min-width: 160px;
      cursor: pointer;
    }
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 575px) {
      grid-template-columns: 1fr;
    }
  }

  .stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    background: var(--surface);
    border: 1px solid $dp-border;
    border-radius: 12px;
    padding: 20px 22px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  .stat-card__icon {
    flex-shrink: 0;
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background: rgba($dp-accent, 0.1);
    color: $dp-accent;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 26px;
    }
  }

  .stat-card__icon--warning {
    background: rgba(#f5a623, 0.12);
    color: #d98c0a;
  }

  .stat-card__icon--danger {
    background: rgba(#e0526a, 0.12);
    color: #d23f59;
  }

  .stat-card__trend {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    margin-top: 6px;
  }

  .stat-card__trend--up {
    color: #2bb673;
  }

  .stat-card__trend--down {
    color: #d23f59;
  }

  .stat-card__body {
    min-width: 0;
    flex: 1 1 auto;
  }

  .stat-card__label {
    font-size: 12px;
    color: $dp-muted;
    margin-bottom: 4px;
  }

  .stat-card__value {
    font-size: 22px;
    font-weight: 700;
    color: $dp-text;
    line-height: 1.2;
  }

  .stat-card__value--text {
    font-size: 15px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stat-card__value--muted {
    color: $dp-muted;
    font-weight: 500;
  }

  .stat-card__meta {
    font-size: 12px;
    color: $dp-muted;
    margin-top: 4px;
  }

  .charts-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 16px;

    @media (max-width: 991px) {
      grid-template-columns: 1fr;
    }
  }

  .chart-card {
    background: var(--surface);
    border: 1px solid $dp-border;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  .chart-card__title {
    font-size: 13px;
    font-weight: 600;
    color: $dp-text;
    margin-bottom: 12px;
  }

  .chart-card--side {
    display: flex;
    flex-direction: column;
  }

  .chart-card__canvas {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
  }

  .chart-card__empty {
    font-size: 13px;
    color: $dp-muted;
    text-align: center;
  }

  .panels-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  .dash-panel {
    min-width: 0;

    .data-table {
      border: 0;
      box-shadow: none;

      thead th,
      thead tr th {
        background: transparent;
        border-bottom: 0;
        border-right: 0;
      }

      tbody td,
      tbody tr td {
        border-top: 0;
        border-right: 0;
      }
    }
  }

  .panel__badge {
    margin-left: auto;
    background: rgba(#e0526a, 0.12);
    color: #d23f59;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 999px;
  }

  .panel__amount {
    margin-left: auto;
    font-size: 12px;
    font-weight: 700;
    color: $dp-accent;
  }

  .data-table__danger {
    color: #d23f59;
    font-weight: 600;
  }

  .top-seller-row {
    background: rgba(#f5c374, 0.12);

    &:hover {
      background: rgba(#f5a623, 0.18);
    }

    .data-table__primary {
      color: #b8860b;
      font-weight: 700;
    }
  }

  .top-seller-icon {
    margin-left: 6px;
    color: #f5a623;
  }
}
</style>
